import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';

export type ButtonStyle = 'basic' | 'outlined' | 'elevated' | 'tonal' | 'icon' | 'bare';

export type ButtonType = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * ZagButton Component
 *
 * A button component with support for multiple styles, types, sizes, icons, and states.
 * Uses content projection for flexible icon and text layouts.
 *
 * @example Basic usage
 * ```html
 * <button zagButton>Click me</button>
 * <button zagButton="basic" zagType="primary">Primary Basic</button>
 * <button zagButton="outlined" zagType="secondary">Outlined Secondary</button>
 * ```
 *
 * @example Style variants
 * ```html
 * <button zagButton>Basic (default)</button>
 * <button zagButton="outlined">Outlined</button>
 * <button zagButton="elevated">Elevated</button>
 * <button zagButton="tonal">Tonal</button>
 * ```
 *
 * @example With icons (using ZagIcon directive)
 * ```html
 * <button zagButton="elevated" zagType="success">
 *   <span zagIcon="fa-check"></span>
 *   Save
 * </button>
 * ```
 *
 * @example Loading state
 * ```html
 * <button zagButton [zagLoading]="isLoading">Submit</button>
 * ```
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'button[zagButton]',
  templateUrl: './button.html',
  styleUrls: ['./button.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.zag-button]': 'buttonStyle() !== "bare"',
    // Style variants
    '[class.zag-button--basic]': 'buttonStyle() === "basic"',
    '[class.zag-button--outlined]': 'buttonStyle() === "outlined"',
    '[class.zag-button--elevated]': 'buttonStyle() === "elevated"',
    '[class.zag-button--tonal]': 'buttonStyle() === "tonal"',
    '[class.zag-button--icon]': 'buttonStyle() === "icon"',
    // Size variants
    '[class.zag-button--sm]': 'buttonStyle() !== "bare" && zagSize() === "sm"',
    '[class.zag-button--md]': 'buttonStyle() !== "bare" && zagSize() === "md"',
    '[class.zag-button--lg]': 'buttonStyle() !== "bare" && zagSize() === "lg"',
    // Type (color) variants
    '[class.zag-button--primary]': 'buttonStyle() !== "bare" && zagType() === "primary"',
    '[class.zag-button--secondary]': 'buttonStyle() !== "bare" && zagType() === "secondary"',
    '[class.zag-button--success]': 'buttonStyle() !== "bare" && zagType() === "success"',
    '[class.zag-button--warning]': 'buttonStyle() !== "bare" && zagType() === "warning"',
    '[class.zag-button--error]': 'buttonStyle() !== "bare" && zagType() === "error"',
    '[class.zag-button--info]': 'buttonStyle() !== "bare" && zagType() === "info"',
    // State variants
    '[class.zag-button--loading]': 'buttonStyle() !== "bare" && zagLoading()',
    '[class.zag-button--disabled]': 'buttonStyle() !== "bare" && isDisabled()',
    '[class.zag-button--collapse]': 'buttonStyle() !== "bare" && zagCollapse()',
    '[attr.disabled]': 'zagLoading() || isDisabled() || null',
  },
})
export class ZagButton {
  private readonly el = inject(ElementRef);

  /**
   * Button style variant (appearance)
   * - basic: Solid fill, no shadow (default)
   * - outlined: Transparent background, colored border
   * - elevated: Solid fill with box-shadow
   * - tonal: Lighter tint background
   * - icon: Borderless icon-only button, no background (neutral at rest, type color on hover/active)
   */
  readonly zagButton = input<ButtonStyle | ''>('');

  /**
   * Computed button style - defaults to 'basic' when empty or not provided
   */
  readonly buttonStyle = computed<ButtonStyle>(() => {
    const style = this.zagButton();
    return style === '' ? 'basic' : style;
  });

  /**
   * Button type/variant for color styling
   */
  readonly zagType = input<ButtonType>('primary');

  /**
   * Button size variant
   */
  readonly zagSize = input<ButtonSize>('md');

  /**
   * Loading state - shows spinner and disables button
   */
  readonly zagLoading = input(false, { transform: booleanAttribute });

  /**
   * Disabled state input - can be used instead of native disabled attribute
   */
  readonly disabled = input(false, { transform: booleanAttribute });

  /**
   * Internal signal to track native disabled attribute
   */
  private readonly nativeDisabled = signal(false);

  /**
   * Computed disabled state - true if either input or native attribute is set
   */
  readonly isDisabled = computed(() => this.disabled() || this.nativeDisabled());

  /**
   * Responsive collapse - hides text on small screens, shows only icon
   */
  readonly zagCollapse = input(false, { transform: booleanAttribute });
}

// Re-export the old Button class as alias for backward compatibility during migration
export { ZagButton as Button };
